import { Injectable } from "@angular/core";

import { StoredDeviceKeyPair } from "./device-trust.types";

const DB_NAME = "gloss-device-trust";
const DB_VERSION = 1;
const STORE_NAME = "deviceKeyPair";
// Single fixed record: today's TDE scope is one device keypair per browser origin
// (see GOOGLE_SSO_PLAN.md, "First device" flow) — not a per-account keypair.
const RECORD_KEY = "device-key-pair";

/**
 * Thin IndexedDB wrapper for the device's TDE keypair. This is the only place in the
 * codebase (as of writing) that talks to IndexedDB directly — there's no existing
 * `AbstractStorageService`-style abstraction for it, and `AbstractStorageService`'s
 * string-keyed get/save/remove shape doesn't fit storing a non-extractable `CryptoKey`
 * (which must round-trip via structured clone, not JSON), so this is deliberately a
 * narrow, feature-scoped wrapper rather than a new cross-cutting storage abstraction.
 */
@Injectable({
  providedIn: "root",
})
export class DeviceKeyStorageService {
  private dbPromise: Promise<IDBDatabase> | null = null;

  async get(): Promise<StoredDeviceKeyPair | null> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(RECORD_KEY);
      request.onsuccess = () => resolve((request.result as StoredDeviceKeyPair) ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  async put(value: StoredDeviceKeyPair): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(value, RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise == null) {
      this.dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          request.result.createObjectStore(STORE_NAME);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
    return this.dbPromise;
  }
}
